import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { Switcher } from 'pepsico-ds';
import {
  ISwitcherProps,
  SwitchItem,
} from 'pepsico-ds/dist/lib/Atoms/Switcher/Switcher';

const Container = styled.div`
  display: inline-block;

  & > span > button[disabled] {
    color: gray !important;
    cursor: not-allowed !important;
    opacity: 0.5 !important;
    pointer-events: none;
  }
`;

export type SwitcherWrapperProps = Omit<
  ISwitcherProps,
  'items' | 'selectedItemIndex' | 'onUpdate' | 'isDark'
> & {
  /** Items available to switch between */
  items?: SwitchItem[];
  /** Default index to select on mount (defaults to 0) - only used in uncontrolled mode */
  defaultIndex?: number;
  /** Controlled selected index - if provided, component operates in controlled mode */
  selectedIndex?: number;
  size?: 'medium' | 'large';
  /** Callback invoked when selection changes */
  onChangeSelection?: (newIndex: number) => void;
};

/**
 * The SwitcherWrapper component.
 *
 * It manages the selected index internally and forwards the appropriate props to the third-party Switcher.
 */
export const SwitcherWrapper: FC<SwitcherWrapperProps> = ({
  items = [],
  defaultIndex = 0,
  selectedIndex: controlledSelectedIndex,
  onChangeSelection,
  ...rest
}) => {
  // Manage the currently selected index via local state (uncontrolled mode)
  const [internalSelectedIndex, setInternalSelectedIndex] =
    useState(defaultIndex);

  // Determine if component is controlled or uncontrolled
  const isControlled = controlledSelectedIndex !== undefined;
  const selectedIndex = isControlled
    ? controlledSelectedIndex
    : internalSelectedIndex;

  // If no items are provided, render a fallback
  if (!items.length) {
    return <div>No items to display.</div>;
  }

  // Ensure the selected index does not exceed the available items
  const safeSelectedIndex = Math.min(selectedIndex, items.length - 1);

  // Handle updates from the third-party Switcher
  const handleUpdate = (newIndex: number) => {
    // Prevent selection of disabled items
    if (items[newIndex]?.disabled) {
      return;
    }
    // Only update internal state if uncontrolled
    if (!isControlled) {
      setInternalSelectedIndex(newIndex);
    }
    onChangeSelection?.(newIndex);
  };

  // Generate a key based on disabled states to force remount when availability changes
  const disabledKey = items.map((item) => (item.disabled ? '1' : '0')).join('');

  return (
    <Container key={`container-${safeSelectedIndex}-${disabledKey}`}>
      <Switcher
        {...rest}
        items={items}
        selectedItemIndex={safeSelectedIndex}
        onUpdate={handleUpdate}
      />
    </Container>
  );
};
